import React, { useEffect, useRef, useState } from 'react';

import { FormikValues } from 'formik';

import { ColumnApi, GridApi, SelectionChangedEvent } from 'ag-grid-community';

import { Divider, LinearProgress, Paper } from '@material-ui/core';
import AddIcon from '@mui/icons-material/Add';
import { Box } from '@mui/material';

import {
    useSelectedDates,
    useSelectedRoute,
    useSelectedRoutes,
} from '../../../../state/ui/planning/routes/index.hooks';
import {
    useSelectedTaskId,
    useSelectedTasks,
    useTasks,
    useTasksSourceSetModel,
    useTasksStatus,
} from '../../../../state/ui/planning/tasks/index.hooks';
import {
    enterAddLocationMode,
    enterTasksPreviewMode,
    resetTasks,
    selectPlanningTask,
    selectTasks,
} from '../../../../state/ui/planning/tasks';

import { useAppDispatch, useToolkitDispatch } from '../../../../hooks';
import TranslationHelper from '../../../../helpers/TranslationHelper';

import { useStyles } from '../../Themable.hooks';

import PaneHeader from '../../../../components/PaneHeader';
import SearchField from '../../../../components/common/SearchField/SearchField';
import SourceSetGrid from '../../../../components/SourceSetGrid';
import { IDragAndDropOptions } from '../../../../components/SourceSetGrid/types';
import IconButtonWithTooltip from '../../../../components/IconButtonWithTooltip';
import { usePrivileges } from '../../../../state/auth/index.hooks';
import {
    hideTopDialog,
    showTaskModificationDialog,
} from '../../../../state/ui/dialog';
import { storeForm } from '../../../../state/ui/forms';
import { getRouteAssigned } from '../../utils';
import isEqual from 'lodash.isequal';

const CHECKBOX_POSITION = 0;

interface IOwnProps {
    handleSetApi: (api: GridApi, columnApi: ColumnApi) => void;
    dragAndDropOptions: () => IDragAndDropOptions;
    handleClearTask: () => void;
    refetchGridsData: () => void;
}

const PlanningTasksGrid = ({
    handleSetApi,
    dragAndDropOptions,
    handleClearTask,
    refetchGridsData,
}: IOwnProps) => {
    const sourceSetModel = useTasksSourceSetModel();

    const [filter, setFilter] = useState(sourceSetModel?.quickFilter || '');
    const classes = useStyles();
    const tasks = useTasks();
    const selectedTasks = useSelectedTasks();
    const dispatch = useAppDispatch();
    const privileges = usePrivileges();
    const status = useTasksStatus();
    const selectedRoutes = useSelectedRoutes();
    const dates = useSelectedDates();
    const selectedTaskId = useSelectedTaskId();
    const toolkitDispatch = useToolkitDispatch();
    const route = useSelectedRoute();

    const routeAssigned = getRouteAssigned(route);

    const prevTaskQueryValues = useRef({ selectedRoutes, dates });
    useEffect(() => {
        const valuesChanged =
            !isEqual(
                prevTaskQueryValues.current.selectedRoutes,
                selectedRoutes
            ) || !isEqual(prevTaskQueryValues.current.dates, dates);
        if (valuesChanged) {
            handleClearTask();
            if (selectedRoutes.length) {
                refetchGridsData();
            } else {
                toolkitDispatch(resetTasks());
            }
        }
        prevTaskQueryValues.current.selectedRoutes = selectedRoutes;
        prevTaskQueryValues.current.dates = dates;
    }, [selectedRoutes, dates]);

    const handleAddLocation = (values: FormikValues) => {
        dispatch(storeForm({ form: values, type: 'task' }));

        dispatch(hideTopDialog());
        toolkitDispatch(enterAddLocationMode());
    };

    const handleCreateTask = () => {
        dispatch(
            showTaskModificationDialog({
                route,
                routeAssigned,
                dates,
                onSuccess: refetchGridsData,
                handleAddLocation,
                mode: 'add',
            })
        );
    };
    const renderRightCustomControls = () => {
        return route ? (
            <>
                {privileges.addTask && (
                    <IconButtonWithTooltip
                        title={TranslationHelper.translate('Add task')}
                        onClick={handleCreateTask}
                    >
                        <AddIcon />
                    </IconButtonWithTooltip>
                )}
            </>
        ) : (
            <></>
        );
    };

    function handleRightPaneVisibilityWhenRowGridSelected(
        selectedTasksIds: any[]
    ) {
        if (!selectedTasksIds.length) {
            handleClearTask();
            dispatch(selectTasks([]));
            return;
        }
        if (selectedTasksIds.length === 1) {
            dispatch(selectPlanningTask(selectedTasksIds[0]));
        } else {
            handleClearTask();
        }
        dispatch(selectTasks(selectedTasksIds));
    }

    const onSelectionChanged = (e: SelectionChangedEvent) => {
        const selectedNodes: any[] = e.api
            .getSelectedNodes()
            .filter(Boolean)
            .map((node) => {
                return node.data.id;
            });
        handleRightPaneVisibilityWhenRowGridSelected(selectedNodes);
    };
    const multiSelectProps = {
        rowSelection: 'multiple',
        checkBox: { index: CHECKBOX_POSITION },
        checkBoxHeader: { index: CHECKBOX_POSITION },
        onSelectionChanged,
        items: selectedTasks.map((item: number) => {
            return { sourceSetId: item };
        }),
    };
    return (
        <Paper className={classes.gridPane}>
            <div className={classes.wrapper}>
                <PaneHeader
                    title={TranslationHelper.translate('Tasks')}
                    renderLeftCustomControls={() => (
                        <>
                            <SearchField
                                value={filter}
                                filterChange={setFilter}
                            />
                        </>
                    )}
                    renderRightCustomControls={() =>
                        renderRightCustomControls()
                    }
                />

                {!selectedRoutes.length && (
                    <Box
                        sx={{
                            p: 1,
                            height: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        {TranslationHelper.translate(
                            'Select route to load tasks'
                        )}
                    </Box>
                )}
                {status === 'error' && selectedRoutes.length && (
                    <Box
                        sx={{
                            p: 1,
                            height: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        {TranslationHelper.translate(
                            'Cannot load tasks for selected route, select different route'
                        )}
                    </Box>
                )}
                {status === 'loading' ? <LinearProgress /> : <Divider />}
                {tasks && (
                    <SourceSetGrid
                        sourceSet={tasks}
                        quickFilterValue={filter}
                        setGridApi={handleSetApi}
                        sourceSetModel={sourceSetModel}
                        selectedSourceSetElementId={selectedTaskId || null}
                        multiSelectProps={multiSelectProps}
                        saveColumnPositions
                        dragAndDropOptions={dragAndDropOptions}
                        actionHandlers={{
                            preview: (params) => {
                                return toolkitDispatch(
                                    enterTasksPreviewMode(params.id)
                                );
                            },
                        }}
                    />
                )}
            </div>
        </Paper>
    );
};

export default PlanningTasksGrid;
